import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

const pendingSelections = new WeakMap();

/**
 * @typedef {object} ProductOptionsRefs
 * @property {HTMLDialogElement} dialog - Native modal dialog.
 * @property {HTMLElement} [guideSource] - Current product's existing sizing content.
 * @property {HTMLElement} [units] - Measurement unit controls.
 * @property {HTMLElement} [modelDetails] - Optional verified profile or existing model information.
 * @property {HTMLElement} [modelContent] - Model profile or fallback paragraph container.
 * @property {HTMLButtonElement} [guideAddButton] - Native form proxy action.
 * @property {HTMLElement} [guideError] - Variant request error message.
 */

/**
 * Native dialog enhancements; option selection remains owned by Ritual's inputs.
 * @property {ProductOptionsRefs} refs - Current dialog and optional sizing elements.
 */
export class ProductOptionsDialog extends DialogComponent {
  #selecting = false;
  #owner;
  #pendingSelection;
  #opening = false;
  #unit = 'cm';
  #selectionFailed = false;
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#owner = this.optionOwner;
    this.#owner?.addEventListener(StandardEvents.productSelect, this.#handleSelection);
    this.addEventListener('dialog:open', this.#handleOpen);
    if (this.#owner) this.#trackSelection(pendingSelections.get(this.#owner));
    this.#prepareGuide();
    this.#syncSizeGuide();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#prepareGuide();
    this.#syncTrigger();
    this.#syncSizeGuide();
  }

  disconnectedCallback() {
    this.#owner?.removeEventListener(StandardEvents.productSelect, this.#handleSelection);
    this.removeEventListener('dialog:open', this.#handleOpen);
    this.#pendingSelection = undefined;
    this.#opening = false;
    this.#submitting = false;
    super.disconnectedCallback();
  }

  #handleOpen = () => { this.#opening = false; };

  /** @param {Event & {promise: Promise<unknown>}} event - Native variant selection. */
  #handleSelection = (event) => {
    if (!event.promise || !this.#owner) return;
    pendingSelections.set(this.#owner, event.promise);
    this.#selectionFailed = false;
    this.#trackSelection(event.promise);
    // Ritual preserves open dialogs during a variant morph. The guide stays open
    // so its next column, price and availability come from the native response.
    if (this.refs.dialog.open && this.dataset.mode !== 'size') this.closeDialog();
  };

  /** @param {Promise<unknown> | undefined} promise - Current native request. */
  #trackSelection(promise) {
    this.#pendingSelection = promise;
    this.#syncTrigger();
    this.#syncSizeGuide();
    if (!promise) return;
    const focusedSize = document.activeElement?.closest?.('button[data-guide-size]')?.dataset.guideSize;
    const settle = (failed) => {
      if (this.#owner && pendingSelections.get(this.#owner) === promise) pendingSelections.delete(this.#owner);
      if (!this.isConnected || this.#pendingSelection !== promise) return;
      this.#pendingSelection = undefined;
      this.#selectionFailed = failed;
      this.updatedCallback();
      this.#syncTrigger();
      if (focusedSize && this.refs.dialog.open &&
        (!document.activeElement || document.activeElement === document.body || document.activeElement === this.refs.dialog)) {
        this.#guideButtons().find((button) => button.dataset.guideSize === focusedSize)?.focus({ preventScroll: true });
      }
    };
    promise.then(() => settle(false), () => settle(true));
  }

  #syncTrigger() {
    for (const trigger of this.optionOwner?.querySelectorAll('[data-options-dialog-trigger]') ?? []) {
      if (trigger.getAttribute('aria-controls') === this.refs.dialog.id) trigger.disabled = !!this.#pendingSelection;
    }
  }

  showDialog() {
    if (this.#pendingSelection || this.#opening || this.refs.dialog.open || !this.isConnected) return;
    this.#opening = true;
    this.#syncSizeGuide();
    super.showDialog();
  }

  get optionOwner() {
    return this.closest('[data-product-options-owner]');
  }

  /** @param {MouseEvent} event - Color button activation. */
  async selectOption(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-option-value-id]') : null;
    if (!button || !this.contains(button) || this.#selecting) return;
    const id = button.dataset.optionValueId;
    this.#selecting = true;
    try {
      await this.closeDialog();
      if (!this.isConnected) return;
      const input = [...(this.optionOwner?.querySelectorAll('input[data-option-value-id]') ?? [])]
        .find((item) => item.dataset.optionValueId === id);
      if (!(input instanceof HTMLInputElement) || input.disabled) return;
      if (!input.checked) input.click();
      input.focus({ preventScroll: true });
    } finally {
      this.#selecting = false;
    }
  }

  #sizeOptions() {
    return [...(this.optionOwner?.querySelectorAll('[data-size-option] input[data-option-value-id], [data-size-option] option[data-option-value-id]') ?? [])];
  }

  #guideButtons() {
    return [...(this.refs.guideSource?.querySelectorAll('button[data-guide-size]') ?? [])];
  }

  /** @param {string} size - Existing size label in the merchant's table. */
  #sizeOption(size) {
    return this.#sizeOptions().find((input) => input.value.trim().toLowerCase() === size.trim().toLowerCase());
  }

  /** @param {MouseEvent} event - Size column activation. */
  selectSize(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-guide-size]') : null;
    if (!button || !this.contains(button) || button.disabled || this.#pendingSelection || this.#submitting) return;
    const input = this.#sizeOption(button.dataset.guideSize);
    if (!input || input.disabled || input.dataset.optionAvailable === 'false') return;
    if (input instanceof HTMLInputElement) {
      if (!input.checked) input.click();
      else if (this.#selectionFailed) input.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (input.parentElement instanceof HTMLSelectElement) {
      input.parentElement.value = input.value;
      input.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.#syncSizeGuide();
  }

  #syncSizeGuide() {
    if (this.dataset.mode !== 'size') return;
    const selected = this.#sizeOptions().find((input) => input.checked || input.selected)?.value;
    for (const cell of this.refs.guideSource?.querySelectorAll('[data-guide-size-cell]') ?? []) {
      cell.dataset.guideSizeSelected = String(cell.dataset.guideSizeCell === selected);
    }
    for (const button of this.#guideButtons()) {
      const input = this.#sizeOption(button.dataset.guideSize);
      const unavailable = !input || input.disabled || input.dataset.optionAvailable === 'false';
      button.dataset.unavailable = String(unavailable);
      button.disabled = unavailable || !!this.#pendingSelection || this.#submitting;
      button.setAttribute('aria-pressed', String(input?.checked || input?.selected || false));
    }
    const { guideAddButton, guideError } = this.refs;
    if (guideAddButton) {
      guideAddButton.disabled = guideAddButton.dataset.variantAvailable !== 'true' ||
        !!this.#pendingSelection || this.#selectionFailed || this.#submitting;
      guideAddButton.setAttribute('aria-busy', String(!!this.#pendingSelection || this.#submitting));
    }
    if (guideError) guideError.hidden = !this.#selectionFailed;
  }

  #productForm() {
    const owner = this.optionOwner;
    const scope = owner?.closest('quick-add-dialog, .shopify-section');
    return [...(scope?.querySelectorAll('product-form-component') ?? [])]
      .find((form) => form.dataset.productId === owner?.dataset.productId);
  }

  async addToCart() {
    if (this.#pendingSelection || this.#selectionFailed || this.#submitting ||
      !this.refs.guideAddButton || this.refs.guideAddButton.disabled) return;
    const variantId = this.refs.guideAddButton.dataset.variantId;
    const form = this.#productForm();
    const button = form?.querySelector('[ref="addToCartButton"]');
    if (!variantId || form?.querySelector('input[name="id"]')?.value !== variantId || !button || button.disabled) return;
    this.#submitting = true;
    this.#syncSizeGuide();
    try {
      await this.closeDialog();
      if (!this.isConnected || this.#pendingSelection || this.#selectionFailed) return;
      // Re-read after the close animation: a concurrent change must never add a stale size.
      const currentForm = this.#productForm();
      const currentButton = currentForm?.querySelector('[ref="addToCartButton"]');
      if (currentForm?.querySelector('input[name="id"]')?.value !== variantId || currentButton?.disabled) return;
      currentButton?.click();
    } finally {
      this.#submitting = false;
      this.#syncSizeGuide();
    }
  }

  /** @param {MouseEvent} event - Unit button activation. */
  changeUnit(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-unit]') : null;
    if (!button || !this.contains(button)) return;
    this.#setUnit(button.dataset.unit === 'in' ? 'in' : 'cm');
  }

  /** @param {'cm' | 'in'} unit - Display unit; source values stay in centimetres. */
  #setUnit(unit) {
    this.#unit = unit;
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    for (const cell of this.refs.guideSource?.querySelectorAll('[data-measurement-cm]') ?? []) {
      cell.textContent = unit === 'in' ? formatter.format(Number(cell.dataset.measurementCm) / 2.54) : cell.dataset.measurementCm;
    }
    for (const button of this.refs.units?.querySelectorAll('button[data-unit]') ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.unit === unit));
    }
  }

  #prepareGuide() {
    const { guideSource, units, modelDetails, modelContent } = this.refs;
    if (!guideSource) return;
    if (modelDetails && modelContent) {
      const modelParagraphs = [...guideSource.querySelectorAll('p')]
        .filter((paragraph) => /^The model\b/i.test(paragraph.textContent.trim()));
      for (const paragraph of modelParagraphs) {
        if (modelDetails.hasAttribute('data-model-profile')) paragraph.remove();
        else modelContent.append(paragraph);
      }
      modelDetails.hidden = modelContent.children.length === 0;
    }
    const sourceTable = guideSource.querySelector('table');
    if (!sourceTable) return;
    if (sourceTable.hasAttribute('data-size-guide-table')) return;
    const rows = [...sourceTable.rows].map((row) => [...row.cells].map((cell) => cell.textContent.trim()));
    const [headings, ...sizes] = rows;
    if (!headings || headings.length < 2 || !sizes.length ||
      headings[0].toLowerCase() !== 'size' ||
      sizes.some((row) => row.length !== headings.length || !row[0] ||
        row.slice(1).some((value) => !/^\d+(?:\.\d+)?$/.test(value)))) return;

    const table = document.createElement('table');
    table.setAttribute('data-size-guide-table', '');
    const thead = table.createTHead();
    const headerRow = thead.insertRow();
    const corner = document.createElement('th');
    corner.scope = 'col';
    corner.textContent = this.dataset.sizeLabel || headings[0];
    headerRow.append(corner);
    for (const size of sizes) {
      const th = document.createElement('th');
      th.scope = 'col';
      th.dataset.guideSizeCell = size[0];
      if (this.#sizeOption(size[0])) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button-unstyled product-options__size';
        button.dataset.guideSize = size[0];
        button.setAttribute('on:click', '/selectSize');
        button.textContent = size[0];
        th.append(button);
      } else {
        th.textContent = size[0];
      }
      headerRow.append(th);
    }
    const tbody = table.createTBody();
    for (let column = 1; column < headings.length; column++) {
      const row = tbody.insertRow();
      const th = document.createElement('th');
      th.scope = 'row';
      th.textContent = headings[column];
      row.append(th);
      for (const size of sizes) {
        const td = row.insertCell();
        td.dataset.guideSizeCell = size[0];
        td.dataset.measurementCm = size[column];
        td.textContent = size[column];
      }
    }
    sourceTable.replaceWith(table);
    if (units) units.hidden = false;
    this.#setUnit(this.#unit);
  }
}

if (!customElements.get('product-options-dialog')) {
  customElements.define('product-options-dialog', ProductOptionsDialog);
}

